import PercentIcon from '@mui/icons-material/Percent';
import PersonIcon from '@mui/icons-material/Person';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import { Grid, Stack } from '@mui/material';
import dayjs from 'dayjs';
import { getSGRWarrantyReport } from 'lib/api/reports';
import { RateBySubProduct } from 'lib/api/reports/types';
import { Filter } from 'lib/common/GlobalSearcher';
import TableCard from 'lib/designSystem/Card/TableCard';
import BarChart from 'lib/designSystem/Charts/BarChart';
import CentralPieChart from 'lib/designSystem/Charts/CentralPieChart';
import LineChart from 'lib/designSystem/Charts/LineChart';
import StatCard from 'lib/designSystem/Charts/StatCard';
import CenteredCircularProgress from 'lib/designSystem/CircularProgress';
import PageHeader from 'lib/designSystem/PageHeader';
import SubtitleWithTooltip from 'lib/designSystem/SubtitleWithTooltip';
import EnhancedTable from 'lib/designSystem/Table';
import CustomToggleButtonGroup from 'lib/designSystem/ToggleButtonGroup';
import { BASE_COLORS, CHART_COLOR_PALETTES } from 'lib/functions/const/colors';
import { formatMoney } from 'lib/functions/utils';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

const SGRWarranty: React.FC = () => {
  const dispatch = useDispatch();
  const startDateDefault = dayjs().startOf('month').format('YYYY-MM-DD');
  const endDateDefault = dayjs().format('YYYY-MM-DD');

  const [startDate, setStartDate] = useState<string>(startDateDefault);
  const [endDate, setEndDate] = useState<string>(endDateDefault);
  const [chartType, setChartType] = useState<string>('placement');
  const [typePieChart, setTypePieChart] = useState<string>('placement');
  const [affiliatedPieChart, setAffiliatedPieChart] =
    useState<string>('economicSegment');
  const [principalPieChart, setPrincipalPieChart] =
    useState<string>('economicSegment');

  const buildQuery = () => {
    const params = new URLSearchParams();
    if (startDate) params.append('startDate', startDate);
    if (endDate) params.append('endDate', endDate);
    return params.toString();
  };

  const [query, setQuery] = useState<string>(buildQuery());

  const { data: report, isPending } = getSGRWarrantyReport(dispatch, query);

  const handleSearch = () => {
    const newQuery = buildQuery();
    setQuery(newQuery);
    return Promise.resolve();
  };

  const filters: Filter[] = [
    {
      label: 'Fecha inicio',
      type: 'date',
      value: startDate,
      setter: setStartDate,
      dateKey: 'startDate',
    },
    {
      label: 'Fecha fin',
      type: 'date',
      value: endDate,
      setter: setEndDate,
      dateKey: 'endDate',
    },
  ];

  const rateRows: RateBySubProduct[] = report?.averageRatesBySubProduct || [];

  if (isPending || !report) return <CenteredCircularProgress type="layout" />;

  return (
    <Stack direction="column" mb={4}>
      <PageHeader.WithTitleAndActions
        title="Reporte SGR"
        subtitle="Aquí podrás ver el reporte de garantías SGR."
        iconType="documents"
        globalSearcherProps={{
          filters,
          triggerSearch: handleSearch,
        }}
      />

      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <StatCard
            label="Colocación activa"
            value={formatMoney(report.kpis.totalPlacementAmount)}
            valueLabel="CLP"
            color={BASE_COLORS.blue}
            icon={<QueryStatsIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatCard
            label="Ganancias totales"
            value={formatMoney(report.kpis.totalIncomeAmount)}
            valueLabel="CLP"
            color={BASE_COLORS.green}
            icon={<QueryStatsIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            label="Duración"
            value={report.kpis.averageDurationDays}
            valueLabel="días"
            color={BASE_COLORS.purple}
            icon={<PercentIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            label="Afianzados"
            value={report.kpis.affiliatedCount}
            valueLabel="afianzados"
            color={BASE_COLORS.orange}
            icon={<PersonIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          <StatCard
            label="Mandantes"
            value={report.kpis.principalCount}
            valueLabel="mandantes"
            color={BASE_COLORS.green}
            icon={<PersonIcon sx={{ fontSize: 24 }} />}
          />
        </Grid>

        <Grid item xs={12} md={8}>
          <TableCard>
            <SubtitleWithTooltip
              title={
                chartType === 'placement'
                  ? 'Colocación en el tiempo'
                  : 'Ingresos en el tiempo'
              }
              tooltipContent={
                chartType === 'placement'
                  ? 'Evolución de colocación a lo largo del tiempo.'
                  : 'Evolución de ingresos a lo largo del tiempo.'
              }
              variant="compact"
            >
              <CustomToggleButtonGroup
                options={[
                  { value: 'placement', label: 'Colocación' },
                  { value: 'income', label: 'Ganancias' },
                ]}
                value={chartType}
                onChange={(value) => setChartType(value)}
              />
            </SubtitleWithTooltip>
            <LineChart
              data={
                chartType === 'placement'
                  ? report.placementOverTime
                  : report.incomeOverTime
              }
              height={350}
            />
          </TableCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Subproductos"
              tooltipContent={
                typePieChart === 'placement'
                  ? 'Colocación por tipo de garantía'
                  : 'Cantidad de garantías por tipo'
              }
              variant="compact"
            >
              <CustomToggleButtonGroup
                options={[
                  { value: 'placement', label: 'Colocación' },
                  { value: 'quantity', label: 'Cantidad' },
                ]}
                value={typePieChart}
                onChange={(value) => setTypePieChart(value)}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                typePieChart === 'placement'
                  ? report.placementBySubProduct
                  : report.countByType
              }
            />
          </TableCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Afianzados"
              tooltipContent={
                affiliatedPieChart === 'economicSegment'
                  ? 'Colocación de afianzados por sector económico.'
                  : 'Colocación de afianzados por tamaño de empresa.'
              }
              variant="compact"
            >
              <CustomToggleButtonGroup
                options={[
                  { value: 'economicSegment', label: 'Sector' },
                  { value: 'companySize', label: 'Tamaño' },
                ]}
                value={affiliatedPieChart}
                onChange={(value) => setAffiliatedPieChart(value)}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                affiliatedPieChart === 'economicSegment'
                  ? report.placementByAffiliatedEconomicSegment
                  : report.placementByAffiliatedCompanySize
              }
            />
          </TableCard>
        </Grid>

        <Grid item xs={12} md={8}>
          <TableCard>
            <SubtitleWithTooltip
              title="Colocación por afianzado"
              tooltipContent="Top 10 afianzados por monto de colocación, desglosado por fecha de vencimiento del monto colocado."
              variant="compact"
            />
            <BarChart
              data={report.placementByAffiliated}
              direction="horizontal"
              yAxisFormat="auto"
              stacked
              colors={CHART_COLOR_PALETTES.orange}
              height={600}
            />
          </TableCard>
        </Grid>
        <Grid item xs={12} md={8}>
          <TableCard>
            <SubtitleWithTooltip
              title="Colocación por mandantes"
              tooltipContent="Distribución de la colocación por mandante, desglosado por fecha de vencimiento del monto colocado."
              variant="compact"
            />
            <BarChart
              data={report.placementByPrincipal}
              direction="horizontal"
              yAxisFormat="auto"
              stacked
              colors={CHART_COLOR_PALETTES.green}
              height={500}
            />
          </TableCard>
        </Grid>

        <Grid item xs={12} md={4}>
          <TableCard>
            <SubtitleWithTooltip
              title="Mandantes"
              tooltipContent={
                principalPieChart === 'economicSegment'
                  ? 'Colocación por mandante segmentada por sector económico.'
                  : 'Colocación por mandante segmentada por tamaño de empresa.'
              }
              variant="compact"
            >
              <CustomToggleButtonGroup
                options={[
                  { value: 'economicSegment', label: 'Sector' },
                  { value: 'companySize', label: 'Tamaño' },
                ]}
                value={principalPieChart}
                onChange={(value) => setPrincipalPieChart(value)}
              />
            </SubtitleWithTooltip>
            <CentralPieChart
              data={
                principalPieChart === 'economicSegment'
                  ? report.placementByPrincipalEconomicSegment
                  : report.placementByPrincipalCompanySize
              }
            />
          </TableCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <TableCard>
            <SubtitleWithTooltip
              title="Colocación por ejecutivo"
              tooltipContent="Distribución de colocación por ejecutivo a cargo."
              variant="compact"
            />
            <CentralPieChart data={report.placementByExecutive} />
          </TableCard>
        </Grid>
        <Grid item xs={12} md={6}>
          <TableCard>
            <SubtitleWithTooltip
              title="Garantías perdidas"
              tooltipContent="Cantidad de garantías en estado perdido por motivo."
              variant="compact"
            />
            <CentralPieChart data={report.lostWarranties} />
          </TableCard>
        </Grid>

        <Grid item xs={12}>
          <TableCard>
            <SubtitleWithTooltip
              title="Subproductos"
              tooltipContent="Promedios de tasas de operación por subproducto."
              variant="compact"
            />
            <EnhancedTable
              data={rateRows}
              dataHeaders={[
                { key: 'subProduct', label: 'Subproducto' },
                {
                  key: 'averageVariableRate',
                  label: 'Tasa descuento (%)',
                  type: 'number',
                },
                {
                  key: 'averageVariableDiscountRate',
                  label: 'Tasa descuento variable (%)',
                  type: 'number',
                },
                {
                  key: 'averageFixedRate',
                  label: 'Tasa fija (UF)',
                  type: 'number',
                },
                {
                  key: 'averageFixedDiscountRate',
                  label: 'Tasa descuento fija (%)',
                  type: 'number',
                },
                {
                  key: 'averageOperationAmount',
                  label: 'Monto promedio (CLP)',
                  type: 'money',
                },
              ]}
            />
          </TableCard>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default SGRWarranty;
